import React, { useEffect, useState, useMemo } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from "@iconify/react/dist/iconify.js";
import axios from "../lib/AxiosGo";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";

const ReporterPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterGender, setFilterGender] = useState("all");
  const [filterHasReports, setFilterHasReports] = useState("all");
  const [selectedReporter, setSelectedReporter] = useState(null);

  const getData = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/reporters/data");
      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching reporter data:", error);
      toast.error("Gagal mengambil data pelapor");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reporterId) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus data pelapor ini? Data pengaduan terkait mungkin ikut terpengaruh.")) return;
    try {
      await axios.delete(`/reporters/${reporterId}`);
      toast.success("Data pelapor berhasil dihapus!");
      if (selectedReporter && selectedReporter.id === reporterId) {
        setSelectedReporter(null);
      }
      getData();
    } catch (error) {
      console.error("Error deleting reporter:", error);
      toast.error("Gagal menghapus data pelapor");
    }
  };

  const handleDownloadLetter = (reportId) => {
    if (!reportId) return;
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/";
    const base = apiUrl.endsWith("/") ? apiUrl : `${apiUrl}/`;
    window.open(`${base}reports/export-pdf?reportId=${reportId}`, "_blank");
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "-";
    let cleaned = phone.replace(/[^0-9]/g, "");
    if (cleaned.startsWith("08")) {
      cleaned = "628" + cleaned.slice(2);
    }
    return cleaned;
  };

  const openWhatsApp = (phone, name) => {
    const formatted = formatPhoneNumber(phone);
    if (!formatted || formatted === "-") {
      toast.warn("Nomor WhatsApp tidak valid");
      return;
    }
    const message = encodeURIComponent(`Halo Bpk/Ibu ${name || "Masyarakat"}, kami dari Subdit IV Renakta Polda Jateng...`);
    window.open(`https://wa.me/${formatted}?text=${message}`, "_blank");
  };

  const handleDirectSendRoute = (reporter) => {
    const phone = reporter.phone || reporter.no_wa || "";
    navigate("/send-message", {
      state: {
        receiver: formatPhoneNumber(phone),
        name: reporter.full_name || "Pelapor",
        deviceId: reporter.device_id || ""
      }
    });
  };

  useEffect(() => {
    getData();
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const name = (item.full_name || "").toLowerCase();
      const phone = (item.phone || item.no_wa || "").toLowerCase();
      const address = (item.address || "").toLowerCase();
      const bot = (item.bot_number || item.device_name || item.device_id || "").toLowerCase();
      const q = searchTerm.toLowerCase().trim();

      const matchSearch =
        !q ||
        name.includes(q) ||
        phone.includes(q) ||
        address.includes(q) ||
        bot.includes(q);

      const matchGender =
        filterGender === "all" ||
        (item.gender || "").toLowerCase() === filterGender.toLowerCase();

      const reportsCount = item.reports ? item.reports.length : 0;
      const matchReports =
        filterHasReports === "all" ||
        (filterHasReports === "with_reports" && reportsCount > 0) ||
        (filterHasReports === "no_reports" && reportsCount === 0);

      return matchSearch && matchGender && matchReports;
    });
  }, [data, searchTerm, filterGender, filterHasReports]);

  // Statistics calculation
  const stats = useMemo(() => {
    const total = data.length;
    const withReports = data.filter((d) => d.reports && d.reports.length > 0).length;
    const maleCount = data.filter((d) => (d.gender || "").toLowerCase().includes("laki")).length;
    const femaleCount = data.filter((d) => (d.gender || "").toLowerCase().includes("perempuan")).length;

    return { total, withReports, maleCount, femaleCount };
  }, [data]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  const getGenderBadge = (gender) => {
    const g = (gender || "").toLowerCase();
    if (g.includes("laki")) {
      return (
        <span className="badge bg-info-subtle text-info-main border border-info-subtle px-8 py-4 radius-6 text-xs d-inline-flex align-items-center gap-1">
          <Icon icon="solar:man-bold" /> Laki-Laki
        </span>
      );
    }
    if (g.includes("perempuan") || g.includes("wanita")) {
      return (
        <span className="badge bg-danger-subtle text-danger-main border border-danger-subtle px-8 py-4 radius-6 text-xs d-inline-flex align-items-center gap-1">
          <Icon icon="solar:women-bold" /> Perempuan
        </span>
      );
    }
    return (
      <span className="badge bg-secondary-subtle text-secondary px-8 py-4 radius-6 text-xs">
        {gender || "-"}
      </span>
    );
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Data Pelapor & Kontak Masyarakat" />

      {/* 1. Metric Stats Cards */}
      <div className="row row-cols-xxxl-4 row-cols-lg-4 row-cols-sm-2 row-cols-1 gy-4 mb-24">
        <div className="col">
          <div className="card shadow-none border bg-gradient-start-1 h-100 radius-12 p-20">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="fw-medium text-secondary text-sm mb-4">Total Pelapor Terdaftar</p>
                <h4 className="fw-bold mb-0 text-dark">{stats.total}</h4>
              </div>
              <div className="w-50-px h-50-px bg-primary-100 text-primary-600 rounded-circle d-flex justify-content-center align-items-center text-2xl">
                <Icon icon="solar:users-group-rounded-bold" />
              </div>
            </div>
          </div>
        </div>

        <div className="col">
          <div className="card shadow-none border bg-gradient-start-2 h-100 radius-12 p-20">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="fw-medium text-secondary text-sm mb-4">Pelapor dgn Laporan Aktif</p>
                <h4 className="fw-bold mb-0 text-dark">{stats.withReports}</h4>
              </div>
              <div className="w-50-px h-50-px bg-success-100 text-success-600 rounded-circle d-flex justify-content-center align-items-center text-2xl">
                <Icon icon="solar:document-bold" />
              </div>
            </div>
          </div>
        </div>

        <div className="col">
          <div className="card shadow-none border bg-gradient-start-3 h-100 radius-12 p-20">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="fw-medium text-secondary text-sm mb-4">Pelapor Laki-Laki</p>
                <h4 className="fw-bold mb-0 text-dark">{stats.maleCount}</h4>
              </div>
              <div className="w-50-px h-50-px bg-info-100 text-info-600 rounded-circle d-flex justify-content-center align-items-center text-2xl">
                <Icon icon="solar:man-bold" />
              </div>
            </div>
          </div>
        </div>

        <div className="col">
          <div className="card shadow-none border bg-gradient-start-4 h-100 radius-12 p-20">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="fw-medium text-secondary text-sm mb-4">Pelapor Perempuan</p>
                <h4 className="fw-bold mb-0 text-dark">{stats.femaleCount}</h4>
              </div>
              <div className="w-50-px h-50-px bg-danger-100 text-danger-600 rounded-circle d-flex justify-content-center align-items-center text-2xl">
                <Icon icon="solar:women-bold" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Main Content Card */}
      <div className="card radius-12 border-0 shadow-sm">
        {/* Header & Filter Bar */}
        <div className="card-header py-16 px-24 bg-base border border-end-0 border-start-0 border-top-0">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h5 className="card-title text-lg text-primary-light mb-2 d-flex align-items-center gap-2">
                <Icon icon="solar:user-id-bold" className="text-primary text-2xl" />
                Daftar Identitas Pelapor Masyarakat
              </h5>
              <p className="text-xs text-muted mb-0">
                Menampilkan <strong>{filteredData.length}</strong> dari total <strong>{data.length}</strong> kontak terdaftar
              </p>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2">
              <Link
                to="/reports"
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 radius-8 px-12"
              >
                <Icon icon="solar:document-text-bold" /> Lihat Semua Pengaduan
              </Link>
              <button
                onClick={getData}
                disabled={loading}
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 radius-8 px-12"
              >
                <Icon icon="solar:refresh-linear" className={loading ? "spin text-md" : "text-md"} /> Refresh
              </button>
            </div>
          </div>

          {/* Search and Filters Controls */}
          <div className="row g-3 mt-12 pt-12 border-top">
            <div className="col-lg-5 col-md-6">
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0 text-secondary">
                  <Icon icon="solar:magnifer-linear" className="text-lg" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0 text-sm"
                  placeholder="Cari nama, nomor WA, alamat domisili, atau nomor bot..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary border-start-0"
                    type="button"
                    onClick={() => setSearchTerm("")}
                  >
                    <Icon icon="solar:close-circle-bold" />
                  </button>
                )}
              </div>
            </div>

            <div className="col-lg-4 col-md-3 col-6">
              <select
                className="form-select text-sm"
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
              >
                <option value="all">Semua Jenis Kelamin</option>
                <option value="Laki-laki">Laki-Laki</option>
                <option value="Perempuan">Perempuan</option>
              </select>
            </div>

            <div className="col-lg-3 col-md-3 col-6">
              <select
                className="form-select text-sm"
                value={filterHasReports}
                onChange={(e) => setFilterHasReports(e.target.value)}
              >
                <option value="all">Semua Status Kasus</option>
                <option value="with_reports">Memiliki Laporan Kasus</option>
                <option value="no_reports">Belum Ada Kasus</option>
              </select>
            </div>
          </div>
        </div>

        {/* 3. Table Data */}
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table border-primary-table mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="ps-20" style={{ width: "70px" }}>ID</th>
                  <th>Identitas Pelapor</th>
                  <th>Kontak WhatsApp / HP</th>
                  <th>Bot Penerima</th>
                  <th>Jenis Kelamin</th>
                  <th>Alamat Domisili</th>
                  <th className="text-center">Total Pengaduan</th>
                  <th>Tgl Registrasi</th>
                  <th className="text-center pe-20" style={{ width: "160px" }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-40 text-muted">
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                      Memuat data kontak pelapor...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((reporter) => {
                    const phone = reporter.phone || reporter.no_wa || "";
                    const reportsCount = reporter.reports ? reporter.reports.length : 0;
                    return (
                      <tr key={reporter.id} className="hover-bg-light transition-1">
                        {/* ID */}
                        <td className="ps-20">
                          <span className="badge bg-dark-subtle text-dark fw-bold px-8 py-4 radius-6 font-monospace text-xs">
                            #{reporter.id}
                          </span>
                        </td>

                        {/* Nama Pelapor */}
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="w-36-px h-36-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center fw-bold text-sm flex-shrink-0">
                              {(reporter.full_name || "M").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <strong className="text-primary-light d-block text-sm">
                                {reporter.full_name || "Masyarakat"}
                              </strong>
                              <span className="text-xs text-muted">
                                Terdaftar di Subdit Renakta
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* No WA */}
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <button
                              onClick={() => openWhatsApp(phone, reporter.full_name)}
                              className="badge bg-success-50 text-success-700 border border-success-subtle px-8 py-4 radius-6 text-xs d-inline-flex align-items-center gap-1 cursor-pointer hover-shadow"
                              title="Buka Chat WhatsApp Web"
                            >
                              <Icon icon="logos:whatsapp-icon" className="text-sm" />
                              <span>{phone || "-"}</span>
                              <Icon icon="solar:arrow-right-up-linear" className="text-xs text-muted" />
                            </button>
                          </div>
                        </td>

                        {/* Bot Penerima */}
                        <td>
                          {reporter.bot_number || reporter.device_name || reporter.device_id ? (
                            <div className="d-flex flex-column">
                              <span className="badge bg-primary-subtle text-primary-main border border-primary-subtle px-8 py-4 radius-6 w-fit d-inline-flex align-items-center gap-1 text-xs">
                                <Icon icon="mdi:robot" /> {reporter.device_name || "Bot Renakta"}
                              </span>
                              <span className="text-xs text-muted mt-2 font-monospace" style={{ fontSize: "11px" }}>
                                {reporter.bot_number ? reporter.bot_number : (reporter.device_id ? `ID: ${reporter.device_id.slice(0, 8)}...` : "")}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted text-xs">-</span>
                          )}
                        </td>

                        {/* Gender */}
                        <td>{getGenderBadge(reporter.gender)}</td>

                        {/* Alamat */}
                        <td style={{ maxWidth: "200px" }}>
                          <div className="text-truncate text-sm text-dark" title={reporter.address}>
                            <Icon icon="solar:map-point-linear" className="text-danger me-1 flex-shrink-0" />
                            {reporter.address || "-"}
                          </div>
                        </td>

                        {/* Total Kasus */}
                        <td className="text-center">
                          {reportsCount > 0 ? (
                            <span className="badge bg-warning-subtle text-warning-main border border-warning-subtle px-10 py-5 radius-6 text-xs fw-bold">
                              {reportsCount} Kasus
                            </span>
                          ) : (
                            <span className="badge bg-light text-muted border text-xs px-8 py-4">
                              0 Kasus
                            </span>
                          )}
                        </td>

                        {/* Tanggal */}
                        <td>
                          <span className="text-xs text-dark d-block">
                            {formatDate(reporter.createdAt)}
                          </span>
                        </td>

                        {/* Action Buttons */}
                        <td className="pe-20 text-center">
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            {/* Detail Profile & Reports */}
                            <button
                              className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 radius-6 px-8 py-4"
                              onClick={() => setSelectedReporter(reporter)}
                              data-bs-toggle="modal"
                              data-bs-target="#reporterDetailModal"
                              title="Lihat Profil & Riwayat Pengaduan"
                            >
                              <Icon icon="solar:eye-bold" />
                            </button>

                            {/* Direct Broadcast / Send WA message in App */}
                            <button
                              className="btn btn-sm btn-success d-inline-flex align-items-center gap-1 radius-6 px-8 py-4"
                              onClick={() => handleDirectSendRoute(reporter)}
                              title="Kirim Pesan WhatsApp Langsung via Bot"
                            >
                              <Icon icon="solar:chat-round-dots-bold" />
                            </button>

                            {/* Delete */}
                            <button
                              className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 radius-6 px-8 py-4"
                              onClick={() => handleDelete(reporter.id)}
                              title="Hapus Data Pelapor"
                            >
                              <Icon icon="solar:trash-bin-trash-bold" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-40 text-muted">
                      <Icon icon="solar:users-group-rounded-outline" className="text-4xl d-block mx-auto mb-8 text-muted opacity-50" />
                      Tidak ditemukan data pelapor yang sesuai filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 4. Modal Detail Profil & Riwayat Kasus Pelapor */}
      {selectedReporter && (
        <div
          className="modal fade show d-block"
          id="reporterDetailModal"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div className="modal-header py-16 px-24 bg-primary text-white radius-top-16 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <Icon icon="solar:user-id-bold" className="text-2xl text-warning" />
                  <div>
                    <h6 className="modal-title text-white fw-bold mb-0">
                      Profil Pelapor #{selectedReporter.id} - {selectedReporter.full_name || "Masyarakat"}
                    </h6>
                    <span className="text-xs text-white-50">Subdit IV Renakta Ditreskrimum Polda Jateng</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedReporter(null)}
                ></button>
              </div>

              <div className="modal-body p-24">
                {/* Header Profil Card */}
                <div className="p-16 radius-12 bg-light border mb-20 d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="w-56-px h-56-px rounded-circle bg-primary-100 text-primary-600 d-flex align-items-center justify-content-center fw-bold text-2xl flex-shrink-0">
                      {(selectedReporter.full_name || "M").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h5 className="mb-2 text-dark fw-bold">{selectedReporter.full_name || "Masyarakat"}</h5>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        {getGenderBadge(selectedReporter.gender)}
                        <span className="badge bg-success-50 text-success-700 border border-success-subtle px-8 py-4 radius-6 text-xs d-inline-flex align-items-center gap-1">
                          <Icon icon="logos:whatsapp-icon" />
                          {selectedReporter.phone || selectedReporter.no_wa || "-"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <button
                      className="btn btn-sm btn-success d-inline-flex align-items-center gap-1 radius-8 px-14 py-8"
                      onClick={() => handleDirectSendRoute(selectedReporter)}
                    >
                      <Icon icon="solar:chat-round-dots-bold" /> Kirim Pesan via Bot
                    </button>
                  </div>
                </div>

                {/* Info Detail Grid */}
                <h6 className="text-sm fw-bold text-primary border-bottom pb-6 mb-12 d-flex align-items-center gap-1">
                  <Icon icon="solar:card-2-bold" /> INFORMASI KONTAK & IDENTITAS
                </h6>
                <div className="row g-3 mb-24 text-sm">
                  <div className="col-sm-6">
                    <span className="text-xs text-secondary d-block">Nomor Telepon / WhatsApp:</span>
                    <strong className="text-dark">{selectedReporter.phone || selectedReporter.no_wa || "-"}</strong>
                  </div>
                  <div className="col-sm-6">
                    <span className="text-xs text-secondary d-block">Terhubung Melalui Bot:</span>
                    <strong className="text-dark">
                      {selectedReporter.device_name || "Bot Renakta"} {selectedReporter.bot_number ? `(${selectedReporter.bot_number})` : ""}
                    </strong>
                  </div>
                  <div className="col-sm-12">
                    <span className="text-xs text-secondary d-block">Alamat Domisili:</span>
                    <div className="p-10 bg-light radius-8 mt-1 text-dark d-flex align-items-center gap-2">
                      <Icon icon="solar:map-point-bold" className="text-danger text-lg flex-shrink-0" />
                      <span>{selectedReporter.address || "Tidak dicantumkan"}</span>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <span className="text-xs text-secondary d-block">Tanggal Pertama Terhubung:</span>
                    <span className="text-dark">{formatDate(selectedReporter.createdAt)}</span>
                  </div>
                </div>

                {/* Riwayat Pengaduan Terkait */}
                <h6 className="text-sm fw-bold text-primary border-bottom pb-6 mb-12 d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-1">
                    <Icon icon="solar:documents-bold" /> RIWAYAT LAPORAN PENGADUAN ({selectedReporter.reports ? selectedReporter.reports.length : 0})
                  </span>
                </h6>

                {selectedReporter.reports && selectedReporter.reports.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {selectedReporter.reports.map((report, idx) => (
                      <div key={report.id || idx} className="p-16 radius-12 border bg-light">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-8">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-dark-subtle text-dark fw-bold px-8 py-4 radius-6 font-monospace text-xs">
                              Laporan #{report.id}
                            </span>
                            <span className="badge bg-primary-subtle text-primary-main border border-primary-subtle px-8 py-4 radius-6 text-xs fw-semibold">
                              {report.report_type_of_crime || "Pengaduan"}
                            </span>
                          </div>
                          <span className="text-xs text-muted">{formatDate(report.createdAt)}</span>
                        </div>

                        <p className="text-sm text-dark mb-8 fw-medium">
                          {report.report_case || "Uraian kasus tidak dicantumkan."}
                        </p>

                        <div className="row g-2 text-xs text-secondary mb-12">
                          <div className="col-sm-6">
                            <strong>TKP:</strong> {report.report_location || "-"}
                          </div>
                          <div className="col-sm-6">
                            <strong>Korban:</strong> {report.report_victim_name || "-"} {report.report_victim_age ? `(${report.report_victim_age} thn)` : ""}
                          </div>
                          <div className="col-sm-6">
                            <strong>Terduga Pelaku:</strong> {report.report_identity_perpetrator || "-"}
                          </div>
                          <div className="col-sm-6">
                            <strong>Lampiran Bukti:</strong> {report.report_attachment ? "Ada Bukti" : "Tidak Ada"}
                          </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-end gap-2 pt-8 border-top">
                          <button
                            className="btn btn-xs btn-danger d-inline-flex align-items-center gap-1 radius-6 px-10 py-5"
                            onClick={() => handleDownloadLetter(report.id)}
                            title="Cetak STPL PDF"
                          >
                            <Icon icon="solar:printer-bold" /> Cetak Surat (STPL)
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-20 text-center text-muted bg-light radius-12">
                    <Icon icon="solar:document-text-outline" className="text-3xl d-block mx-auto mb-4 opacity-50" />
                    <p className="text-xs mb-0">Pelapor ini belum memiliki riwayat formulir pengaduan tersimpan.</p>
                  </div>
                )}
              </div>

              <div className="modal-footer py-14 px-24 bg-light d-flex align-items-center justify-content-between">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-16 radius-8"
                  onClick={() => setSelectedReporter(null)}
                >
                  Tutup
                </button>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-success btn-sm px-16 radius-8 d-flex align-items-center gap-1"
                    onClick={() => {
                      openWhatsApp(selectedReporter.phone || selectedReporter.no_wa, selectedReporter.full_name);
                    }}
                  >
                    <Icon icon="logos:whatsapp-icon" /> Chat WhatsApp Web
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default ReporterPage;
